import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Wallet, TrendingUp, RefreshCw, ExternalLink, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserBalance } from "@/hooks/useBalance";

interface SaldoWidgetProps {
  showLink?: boolean;
  size?: 'small' | 'medium' | 'large';
  showActions?: boolean;
  className?: string;
  hideIcon?: boolean;
}

const SaldoWidget: React.FC<SaldoWidgetProps> = ({ 
  showLink = true,
  size = 'medium',
  showActions = false,
  className = "",
  hideIcon = false
}) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { saldo: hookSaldo, loading: saldoLoading, error, refreshBalance, lastUpdated } = useUserBalance();
  const [shouldAnimate, setShouldAnimate] = useState(false);

  // Priorizar saldo do hook, depois do user, depois 0
  const saldo = hookSaldo || user?.saldo || 0;
  const loading = authLoading || saldoLoading;

  

  // Função robusta para converter saldo para número
  const parseNumericValue = (value: unknown): number => {
    // Se já é um número válido, retorna direto
    if (typeof value === 'number' && !isNaN(value)) return value;
    
    // Se é string, tenta converter
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = parseFloat(value.trim());
      return !isNaN(parsed) ? parsed : 0;
    }
    
    // Qualquer outro caso retorna 0
    return 0;
  };

  const saldoNumerico = parseNumericValue(saldo);

  

  useEffect(() => {
    if (showActions) {
      const timer = setTimeout(() => {
        setShouldAnimate(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showActions]);

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return {
          container: 'p-2',
          valor: 'text-base font-bold',
          label: 'text-[10px]',
          icon: 'w-4 h-4'
        };
      case 'large':
        return {
          container: 'p-6',
          valor: 'text-3xl font-bold',
          label: 'text-base',
          icon: 'w-6 h-6'
        };
      default: // medium
        return {
          container: 'p-4',
          valor: 'text-xl font-bold',
          label: 'text-sm',
          icon: 'w-5 h-5'
        };
    }
  };

  const sizeClasses = getSizeClasses();

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Card className={`bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 ${className} ${size === 'small' ? 'w-36' : ''}`}>
      <CardContent className={sizeClasses.container}>
        <div className="flex items-center justify-between">
          <div className={`flex items-center ${hideIcon ? 'space-x-2' : 'space-x-3'}`}>
            {!hideIcon && (
              <div className="p-2 bg-green-100 rounded-full">
                <Wallet className={`${sizeClasses.icon} text-green-600`} />
              </div>
            )}
            <div>
              <p className={`${sizeClasses.label} text-gray-600 font-medium`}>
                Saldo da Carteira
                {lastUpdated && size !== 'small' && (
                  <span className="text-xs text-gray-400 ml-2">
                    Atualizado há {Math.round((Date.now() - lastUpdated.getTime()) / 1000 / 60)}min
                  </span>
                )}
              </p>
              <div className="flex items-center space-x-2">
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
                    <span className={`${sizeClasses.valor} text-gray-400`}>
                      Carregando...
                    </span>
                  </div>
                ) : error ? (
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className={`${sizeClasses.valor} text-red-600`}>
                      Erro
                    </span>
                  </div>
                ) : (
                  <span className={`${sizeClasses.valor} text-green-700`}>
                    R$ {saldoNumerico.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {saldoNumerico < 100 && size !== 'small' && (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 animate-pulse">
                🔴 Saldo Crítico
              </Badge>
            )}
            
            {/* Botão de refresh manual */}
            {size !== 'small' && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={refreshBalance}
                disabled={loading}
                className="text-gray-500 hover:text-gray-700"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            )}

            {showActions && (
              <Button variant="outline" size="sm" asChild>
                <Link to="/client/creditos">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  Recarregar
                </Link>
              </Button>
            )}

            {showLink && (
              <Button variant="ghost" size="sm" asChild>
                <Link to="/client/creditos" className="text-green-600 hover:text-green-700">
                  <ExternalLink className={sizeClasses.icon} />
                </Link>
              </Button>
            )}
          </div>
        </div>

        {/* Indicador de status do saldo */}
        {size !== 'small' && (
          <div className="mt-3 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className={`h-2 rounded-full transition-all duration-1000 ease-out transform ${
                saldoNumerico < 100 ? 'bg-gradient-to-r from-red-500 to-red-600 animate-pulse' : 
                saldoNumerico < 500 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' : 
                'bg-gradient-to-r from-green-400 to-green-600'
              }`}
              style={{ 
                width: shouldAnimate ? `${Math.min((saldoNumerico / 1000) * 100, 100)}%` : '0%',
                transition: 'width 1.5s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            />
          </div>
        )}
        
        {size !== 'small' && (
          <p className="text-xs text-gray-500 mt-2">
            {saldoNumerico < 100 ? '🔴 Saldo crítico - Recarregue urgente!' :
             saldoNumerico < 500 ? '⚠️ Considere recarregar em breve' :
             '✅ Saldo adequado para reservas'}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default SaldoWidget; 
